import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { api, errText, downloadFile } from "../../lib/api";
import { MagnifyingGlass, Plus, UploadSimple } from "@phosphor-icons/react";
import { EmployeeTable } from "../../components/admin/employees/EmployeeTable";
import { AddEmployeeModal } from "../../components/admin/employees/AddEmployeeModal";

export default function Employees() {
  const [list, setList] = useState([]);
  const [deps, setDeps] = useState([]);
  const [search, setSearch] = useState("");
  const [dep, setDep] = useState("");
  const [show, setShow] = useState(false);
  const [importing, setImporting] = useState(false);
  const importRef = useRef();

  const load = useCallback(() => api.get("/employees", { params: { search, department: dep } }).then((r) => setList(r.data)), [search, dep]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/departments").then((r) => setDeps(r.data)); }, []);

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/employees/import", fd);
      toast.success(r.data.message);
      if (r.data.errors?.length) toast.warning(r.data.errors.slice(0, 3).join("; "));
      load();
    } catch (err) { toast.error(errText(err)); } finally { setImporting(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Employees</h1>
          <p className="text-stone-500 text-sm mt-1">{list.length} people in directory</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={importRef} type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="import-csv-input" />
          <button onClick={() => importRef.current?.click()} disabled={importing} data-testid="import-csv-button"
            className="tap-scale flex items-center gap-2 bg-white border border-stone-300 text-stone-700 font-bold px-5 py-3 rounded-full hover:bg-stone-50 transition-colors duration-200 disabled:opacity-60">
            <UploadSimple size={18} weight="bold" /> {importing ? "Importing…" : "Import CSV"}
          </button>
          <button onClick={() => downloadFile("/employees/import/template", "employee_import_template.csv").catch((e) => toast.error(errText(e)))}
            data-testid="download-template-button" className="text-xs font-bold text-primary self-center hover:underline">Template</button>
          <button onClick={() => setShow(true)} data-testid="add-employee-button"
            className="tap-scale flex items-center gap-2 bg-primary text-white font-bold px-6 py-3 rounded-full hover:bg-[#A63F07] transition-colors duration-200">
            <Plus size={18} weight="bold" /> Add employee
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or code…" data-testid="employee-search-input"
            className="w-full min-h-[48px] pl-11 pr-4 rounded-lg border border-stone-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
        </div>
        <select value={dep} onChange={(e) => setDep(e.target.value)} data-testid="employee-department-filter"
          className="min-h-[48px] px-4 rounded-lg border border-stone-300 bg-white">
          <option value="">All departments</option>
          {deps.map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
        </select>
      </div>

      <EmployeeTable list={list} />

      {show && <AddEmployeeModal deps={deps} onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
    </div>
  );
}
